
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { bulkArchive, bulkExport, bulkResend, bulkSign, getMassiveStatus, bulkCancel, bulkFinalize } from 'api/actions/contracts';
import apiClient, { fetcher } from 'api/client';
import { toast } from 'components/Shared/Toast'
import { useContractList, SET_SELECTED_CONTRACTS, SET_MODAL, SET_META, OPEN_CREATE_CONTRACT, useContractListState } from 'context/contracts';
import bootbox from 'components/Modals/Bootbox';
import useFolderActions from 'hooks/useFolderActions';
import useDebounce from 'hooks/useDebounce';
import { useProfileAbility, useProfileState } from 'context/profile';
import { useChannel, useEvent } from '@harelpls/use-pusher';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import { useMutation, useQuery } from 'react-query';
import {
    ButtonGroup,
    Button,
    Input,
    InputGroup,
    UncontrolledTooltip
} from 'reactstrap';
import { useParams } from 'react-router-dom';
import { archiverFolder } from 'app-constants';
import { MainHeader } from 'components/Shared/MainHeader';
import { downloadFile } from 'utils';
import useContractActions from 'hooks/useContractActions';
import useWhoami from 'hooks/useWhoami';

function HeaderToolbar({ onSearchTypeChange, onSearch, onNewContract, onNewFolder, listOnly, pendingContractActions, signProcess }) {
    const { t } = useTranslation(['translation', 'validations', 'glossary']);
    const [{ query: refetchList, meta, currentRecord, selectedContracts, query, contracts }, dispatch] = useContractList()
    const { profile } = useProfileState();
    const ability = useProfileAbility();
    const { childOf } = useParams();
    const { getWhoamiFor } = useWhoami()

    const channel = useChannel(profile._id && `private-${profile._id}`);
    useEvent(channel, 'sign-all-ended', () => {
        refetchList();
    });

    const onSuccess = () => {
        query();
        dispatch({ type: SET_SELECTED_CONTRACTS, selected: [] })
    }
    const onError = (error) => {
        toast.error(t(`errors.${error}`));
    }

    const { mutate: bulkArchiveMutate } = useMutation(() => fetcher(bulkArchive(selectedContracts)), { onSuccess, onError });
    const { mutate: bulkSignMutate } = useMutation(() => fetcher(bulkSign(selectedContracts)), { onSuccess });
    const { mutate: bulkResendMutate } = useMutation(() => fetcher(bulkResend(selectedContracts)), { onSuccess, onError });
    const { mutate: bulkCancelMutate } = useMutation((payload) => fetcher(bulkCancel({ ids: selectedContracts, ...payload })), { onSuccess, onError });
    const { mutate: bulkFinalizeMutate } = useMutation((payload) => fetcher(bulkFinalize({ ids: selectedContracts, ...payload })), { onSuccess, onError });
    const { mutate: mutateBulkExport } = useMutation((payload) => fetcher(bulkExport(payload)), {
        onSuccess: (data) => {
            downloadFile({
                suffix: new Date().getTime(),
                name: 'Export',
                ext: 'csv',
                blob: new Blob(['\ufeff', data])
            })();
        }, onError
    });

    const showBulkCancel = useMemo(() => {
        return contracts.filter(c => selectedContracts.includes(c._id)).every(c => {
            const whoami = getWhoamiFor(c);
            return !whoami.isSigned() && !whoami.isFinalized() && !whoami.isArchived() && !whoami.isCanceled() && whoami.isOwner() && !whoami.isFolder();
        })
    }, [selectedContracts, contracts, getWhoamiFor])

    const showBulkFinalize = useMemo(() => {
        return contracts.filter(c => selectedContracts.includes(c._id)).every(c => {
            const whoami = getWhoamiFor(c);

            return (whoami.canBeFinalized() || whoami.contractIsApplication()) && !whoami.isFinalized() && !whoami.isArchived() && !whoami.isCanceled() && whoami.isOwner();
        })
    }, [selectedContracts, contracts, getWhoamiFor])

    const handleBulkResend = async () => {
        if (!selectedContracts.length) return toast.error(t('contracts.listing.select_one'));
        if (await bootbox.confirm(t('contracts.listing.resend'))) {
            bulkResendMutate({}, {
                onSuccess: () => {
                    toast.success(t('contracts.listing.bulk_resend_success'))
                }
            })
        }
    }

    const handleBulkSendToFolder = async () => {
        dispatch({ type: SET_MODAL, name: 'MOVE_TO', isBulk: true })
    }

    const handleBulkCancel = async () => {
        const cancelReason = await bootbox.prompt(t('contracts.listing.bulk-cancel-question'));
        if (cancelReason === null) return;
        bulkCancelMutate({ cancelReason }, {
          onSuccess: (actionResult) => {
            toast.success(t('contracts.listing.bulk_cancel_success'));
          }
        });
    }

    const handleBulkFinalize = async () => {
        
        const confirm = await bootbox.confirm(t('contracts.listing.bulk-finalize-question'));
        if (!confirm) return;

        bulkFinalizeMutate({}, {
            onSuccess: (actionResult) => {
                toast.success(t('contracts.listing.bulk_finalize_success'));
            }
        });
    }

    

    const handleBulkSign = async () => {
        if (signProcess) return null;
        if (!selectedContracts.length) return toast.error(t('contracts.listing.select_one'));

        if (await bootbox.confirm(t('contracts.listing.confirm_signall',
            { replace: { selectedContracts: contracts.filter(c => selectedContracts.includes(c._id)).map(c => c.documentId).join(', ') } }
        ))) {
            bulkSignMutate({}, {
                onSuccess: () => {
                    bootbox.alert(t('contracts.listing.signallStart', { replace: { contractsToSign: selectedContracts.length } }))
                }, onError: (error) => {
                    // toast.error(errorMessage.concat(executingProcess?.progress ? `${executingProcess?.progress}%` : ''));
                }
            })
        }
    }
    const handleBulkArchive = async () => {
        if (!selectedContracts.length) return toast.error(t('contracts.listing.select_one'));
        if (await bootbox.confirm(t('contracts.archive.modal'))) {
            bulkArchiveMutate({}, {
                onSuccess: () => {
                    toast.success(t('contracts.listing.bulk_archive_success'))
                }
            })
        }
    }
    const exportContract = () => {
        mutateBulkExport(`${meta.url}?isExport=true`)
    }

    const hideCreateFolder = meta.recordId?.length || meta.searchTerm || meta.listOnly;
    const hideCreateContract = meta.searchTerm || meta.listOnly;
    const headerTitle = currentRecord?.name ? currentRecord.name : meta.listOnly ? 'incoming.title' : 'contracts.title'

    return (
        <MainHeader title={meta.searchTerm ? `dashboard.counter.${meta.searchTerm}` : headerTitle}>
            <InputGroup inlist="true" className="align-items-center">
                <Input type="select" className="p-0 mr-1" onChange={onSearchTypeChange}>
                    <option value="name">{t('contracts.search.name')}</option>
                    <option value="documentId">Id</option>
                    <option value="content">{t('contracts.search.content')}</option>
                    <option value="tags">{t('contracts.search.tags')}</option>
                    <option value="participants">{t('contracts.search.participants')}</option>
                    <option value="externalId">{t('editor.config.externalId')}</option>
                </Input>
                <Input type="text" placeholder={t('contracts.search.placeholder')} onChange={onSearch} />

                <ButtonGroup>
                    {/* Bulk send to Folder*/}
                    <UncontrolledTooltip target="bulk-send-to-folder">{t('contracts.listing.bulk-send-to-folder')}</UncontrolledTooltip>
                    <Button color="white" id="bulk-send-to-folder" hidden={!!listOnly || !selectedContracts.length} onClick={handleBulkSendToFolder}>
                        <i className="fas fa-folder-open fa-2x"></i>
                    </Button>
                    {/* Bulk send to Folder*/}
                    <UncontrolledTooltip target="bulk-finalize">{t('contracts.listing.bulk-finalize')}</UncontrolledTooltip>
                    <Button color="white" id="bulk-finalize" hidden={!!listOnly || !selectedContracts.length || !showBulkFinalize} onClick={handleBulkFinalize}>
                        <i className="fas fa-lock fa-2x"></i>
                    </Button>
                    {/* Bulk Cancel*/}
                    <UncontrolledTooltip target="bulk-cancel">{t('contracts.listing.bulk-cancel')}</UncontrolledTooltip>
                    <Button color="white" id="bulk-cancel" hidden={!!listOnly || !selectedContracts.length || !showBulkCancel} onClick={handleBulkCancel}>
                        <i className="fas fa-ban fa-2x"></i>
                    </Button>
                    {/* Bulk Archive*/}
                    <UncontrolledTooltip target="bulk-archive">{t('contracts.listing.bulk-archive')}</UncontrolledTooltip>
                    <Button color="white" id="bulk-archive" hidden={!!listOnly || childOf === archiverFolder} onClick={handleBulkArchive}>
                        <i className="fas fa-archive fa-2x"></i>
                    </Button>
                    {/* Bulk Resend*/}
                    <UncontrolledTooltip target="bulk-resend">{t('contracts.listing.bulk-resend')}</UncontrolledTooltip>
                    <Button color="white" id="bulk-resend" onClick={handleBulkResend} hidden={!(pendingContractActions || meta.miniDashboardFilter === 'Send')}>
                        <i className="fas fa-paper-plane fa-2x"></i>
                    </Button>
                    {/* Bulk Sign*/}
                    <UncontrolledTooltip target="bulk-sign">{t('contracts.listing.bulk-sign')}</UncontrolledTooltip>
                    <Button color="white" id="bulk-sign" onClick={handleBulkSign} hidden={!pendingContractActions}>
                        {!signProcess || !signProcess.success ?
                            <i className="fas fa-signature fa-2x"></i>
                            :
                            <div style={{ width: 40, height: 40 }}>
                                <CircularProgressbar
                                    value={signProcess?.executingProcess?.progress}
                                    text={`${signProcess?.executingProcess?.progress}%`}
                                    circleRatio={0.75}
                                    styles={buildStyles({
                                        rotation: 1 / 2 + 1 / 8,
                                        strokeLinecap: 'butt',
                                        // trailColor: "#eee"
                                        pathColor: '#F47B33',
                                        textColor: '#F47B33'
                                    })}
                                />
                            </div>
                        }
                    </Button>
                    {/* Create Folder*/}
                    <UncontrolledTooltip target="create-folder">{t('contracts.listing.create-folder')}</UncontrolledTooltip>
                    <Button color="default" id="create-folder" onClick={onNewFolder} hidden={hideCreateFolder}>
                        <i className="fas fa-folder-plus fa-2x"></i>
                    </Button>
                    {/* Export Contracts*/}
                    <UncontrolledTooltip target="export-contracts">{t('contracts.documents.download')}</UncontrolledTooltip>
                    <Button color="default" id="export-contracts" onClick={exportContract} hidden={!meta.searchTerm || ability.cannot('DOWNLOAD_REPORT_IN_CONTRACT_LIST')}>
                        <i className="fas fa-download fa-2x"></i>
                    </Button>
                    {/* Create Contract*/}
                    <UncontrolledTooltip target="create-contract">{t('contracts.listing.create-contract')}</UncontrolledTooltip>
                    <Button color="default" id="create-contract" onClick={onNewContract} hidden={hideCreateContract}>
                        <i className="fas fa-plus fa-2x text-primary"></i>
                    </Button>
                </ButtonGroup>
            </InputGroup>
        </MainHeader>
    )
};

function HeaderToolbarContainer() {
    const [state, dispatch] = useContractList();
    const listOnly = state.meta.listOnly;
    const [searchType, setSearchType] = useState('name');
    const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 500);
    const isPendingContract = state.meta.searchTerm === 'pendingContracts'

    const { data } = useQuery('get-massive', () => fetcher(getMassiveStatus), { refetchInterval: 5000, refetchIntervalInBackground: true, enabled: !!isPendingContract });

    const createFolderAction = useFolderActions({}, 'CREATE', {
        returnAsObject: true,
        refetch: state.query
    });

    const handleSearch = (evt) => {
        // debounce
        const target = evt.target;
        setSearch(target.value)
    }

    useEffect(() => {
        dispatch({
            type: SET_META, meta: {
                search: `${searchType}|${debouncedSearch}`,
                page: 1
            }
        })
    }, [searchType, debouncedSearch, dispatch])

    const handleSearchTypeChange = e => setSearchType(e.target.value)

    const handleNewContract = () => {
        dispatch({ type: OPEN_CREATE_CONTRACT })
    }

    return <HeaderToolbar
        onNewContract={handleNewContract}
        onNewFolder={createFolderAction.onClick}
        onSearch={handleSearch}
        onSearchTypeChange={handleSearchTypeChange}
        listOnly={listOnly}
        pendingContractActions={isPendingContract}
        signProcess={data}
    />
}

export default HeaderToolbarContainer;